import { useState, useCallback, useEffect } from 'react';
import { X, Download, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Modal de previsualisation de documents (images + PDF)
 * Usage:
 *   const { previewDocument, DocumentModal } = useDocumentPreview();
 *   // Dans un onClick: previewDocument(documentId, filename)
 *   // Dans le JSX: <DocumentModal />
 */
export function useDocumentPreview() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const previewDocument = useCallback(async (documentId, filename, customEndpoint) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const endpoint = customEndpoint || `/documents/file/${documentId}`;

    try {
      const res = await axios.get(endpoint, {
        responseType: 'blob',
        params: { token },
      });

      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const isImage = contentType.startsWith('image/');
      const isPDF = contentType.includes('pdf');

      setPreview({ url, filename: filename || 'Document', isImage, isPDF, contentType, documentId, endpoint });
    } catch {
      if (customEndpoint) toast.error("Impossible d'afficher le document");
      else window.open(`${baseUrl}/api${endpoint}?token=${token}`, '_blank');
    }
    setLoading(false);
  }, []);

  const close = useCallback(() => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }, [preview]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, close]);

  const download = useCallback(() => {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = preview.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [preview]);

  // Ouvre le fichier déjà chargé (clic direct : non bloqué, même sur iPhone)
  const openExternal = useCallback(() => {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [preview]);

  const DocumentModal = useCallback(() => {
    if (loading) {
      return (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center" data-testid="doc-preview-loading">
          <Loader2 className="h-10 w-10 text-white animate-spin" />
        </div>
      );
    }
    if (!preview) return null;

    return (
      <div
        className="fixed inset-0 bg-black/80 z-[10000] flex flex-col"
        onClick={close}
        data-testid="doc-preview-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-black/40 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <p className="text-white text-sm font-medium truncate max-w-[60%]">{preview.filename}</p>
          <div className="flex items-center gap-1">
            <button onClick={download} className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="Telecharger">
              <Download className="h-5 w-5" />
            </button>
            <button onClick={openExternal} className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="Ouvrir dans un nouvel onglet">
              <ExternalLink className="h-5 w-5" />
            </button>
            <button onClick={close} className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="doc-preview-close">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto" onClick={(e) => e.stopPropagation()}>
          {preview.isImage && (
            <img
              src={preview.url}
              alt={preview.filename}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          )}
          {preview.isPDF && (
            <iframe
              src={preview.url}
              title={preview.filename}
              className="w-full h-full max-w-4xl rounded-lg bg-white"
            />
          )}
          {!preview.isImage && !preview.isPDF && (
            <div className="text-center text-white space-y-4">
              <p className="text-lg">Apercu non disponible pour ce type de fichier</p>
              <p className="text-sm text-white/60">{preview.contentType}</p>
              <button
                onClick={download}
                className="px-6 py-2 bg-white text-slate-800 rounded-lg font-medium hover:bg-slate-100 transition-colors"
              >
                Telecharger le fichier
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }, [preview, loading, close, download, openExternal]);

  return { previewDocument, DocumentModal };
}
